import React, { useContext, useCallback, useEffect, useState, useRef } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';
import { NudgeSpotlight } from '@atlassiansox/nudge-tooltip';

import { layers } from '@atlaskit/theme/constants';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { markErrorAsHandled } from '@confluence/graphql';
import {
	ONBOARDING_NUDGE_EXPERIENCE,
	ExperienceTrackerContext,
	ExperienceFailure,
	ExperienceSuccess,
} from '@confluence/experience-tracker';
import {
	GenericErrorBoundary,
	Attribution,
	ErrorDisplay,
	isUnauthorizedError,
} from '@confluence/error-boundary';
import {
	useSetOnboardingState,
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { NTH_USER_SETUP_CONTENT_TYPE } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useIsNthUserContentTypePageEnabled } from '@confluence/onboarding-helpers/entry-points/hooks/useIsNthUserContentTypePageEnabled';
import { useChoreographerAssets } from '@confluence/choreographer-services';

import { fireEvent } from './AnalyticEvent';

type NudgeProps = React.ComponentProps<typeof NudgeSpotlight>;
type NudgeSpotlightRef = {
	toggleCardVisibility?: (visible: boolean) => void;
	forceUpdateCardPosition?: () => void;
};

type ContentType = 'live-doc' | 'page' | 'whiteboard' | 'database';

export type OnboardingNudgeProps = {
	source: string;
	actionSubjectId: string;
	wrapper?: any;
	renderChildrenWhileLoading?: boolean;
	onboardingErrors?: Error[];
	contentType: ContentType;
} & NudgeProps;

export const ELEMENTS_NOT_IN_NAV = {};

// Timer helper function used to help with rerendering OnboardingNudgeTooltip
export const autoAdjustPosition = (
	fn: Function | undefined,
	{ interval = 500, cycles = 10 }: { interval: number; cycles: number },
) => {
	let i = 0;
	const timer = setInterval(() => {
		fn?.();
		i++;
		if (i === cycles) {
			clearInterval(timer);
		}
	}, interval);
	return timer;
};

function isErrorHandled(error: Parameters<typeof markErrorAsHandled>[0]) {
	// Mark unauthorized error as handled and not report to monitoring client
	if (isUnauthorizedError(error) || error?.graphQLErrors?.[0]?.message === 'unauthorized') {
		markErrorAsHandled(error);
		return true;
	}
	return false;
}

const i18n = defineMessages({
	popularHeading: {
		id: 'onboarding-nudge-tooltip.nth-user.content-types.popular.heading',
		defaultMessage: 'This is the most popular {contentType} in your site',
		description:
			'Heading for a nudge around a page, live doc, whiteboard, or database highlighting the most popular content',
	},
	nonPopularHeading: {
		id: 'onboarding-nudge-tooltip.nth-user.content-types.non-popular.heading',
		defaultMessage: 'This is a sample {contentType} in your space',
		description:
			'Heading for a nudge around a page, live doc, whiteboard, or database highlighting the most popular content',
	},
	contentDescription: {
		id: 'onboarding-nudge-tooltip.nth-user.content-types.content.description',
		defaultMessage:
			'Take a quick look at the {contentType} and learn how Confluence {pluralContentType} work.',
		description:
			'Content description for a nudge around a page, live doc, whiteboard, or database highlighting the most popular content',
	},
	pageContentType: {
		id: 'onboarding-nudge-tooltip.nth-user.content-types.page.content-type',
		defaultMessage: 'page',
		description: 'Text in a nudge describing the content type the user is looking at',
	},
	liveDocContentType: {
		id: 'onboarding-nudge-tooltip.nth-user.content-types.live-doc.content-type',
		defaultMessage: 'live doc',
		description: 'Text in a nudge describing the content type the user is looking at',
	},
	whiteboardContentType: {
		id: 'onboarding-nudge-tooltip.nth-user.content-types.whiteboard.content-type',
		defaultMessage: 'whiteboard',
		description: 'Text in a nudge describing the content type the user is looking at',
	},
	databaseContentType: {
		id: 'onboarding-nudge-tooltip.nth-user.content-types.database.content-type',
		defaultMessage: 'database',
		description: 'Text in a nudge describing the content type the user is looking at',
	},
	pluralPageContentType: {
		id: 'onboarding-nudge-tooltip.nth-user.content-types.plural.page.content-type',
		defaultMessage: 'pages',
		description:
			'Text in a nudge describing the plural version of the content type the user is looking at',
	},
	pluralLiveDocContentType: {
		id: 'onboarding-nudge-tooltip.nth-user.content-types.plural.live-doc.content-type',
		defaultMessage: 'live docs',
		description:
			'Text in a nudge describing the plural version of the content type the user is looking at',
	},
	pluralWhiteboardContentType: {
		id: 'onboarding-nudge-tooltip.nth-user.content-types.plural.whiteboard.content-type',
		defaultMessage: 'whiteboards',
		description:
			'Text in a nudge describing the plural version of the content type the user is looking at',
	},
	pluralDatabaseContentType: {
		id: 'onboarding-nudge-tooltip.nth-user.content-types.plural.database.content-type',
		defaultMessage: 'databases',
		description:
			'Text in a nudge describing the plural version of the content type the user is looking at',
	},
});

const contentTypeMapping = (contentType: ContentType, isPlural: boolean = false) => {
	const mapping = {
		page: {
			singular: i18n.pageContentType,
			plural: i18n.pluralPageContentType,
		},
		'live-doc': {
			singular: i18n.liveDocContentType,
			plural: i18n.pluralLiveDocContentType,
		},
		whiteboard: {
			singular: i18n.whiteboardContentType,
			plural: i18n.pluralWhiteboardContentType,
		},
		database: {
			singular: i18n.databaseContentType,
			plural: i18n.pluralDatabaseContentType,
		},
	}[contentType];

	return isPlural ? mapping.plural : mapping.singular;
};

const actionSubjectId = 'contentTypeNthUserOnboardingNudge';

export const OnboardingNthUserContentNudge: React.FC<OnboardingNudgeProps> = (props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const { useChoreographedRender } = useChoreographerAssets();
	const { setOnboardingState } = useSetOnboardingState();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const {
		children,
		source = 'onboarding',
		width = 275,
		wrapper,
		zIndex = layers.blanket(),
		position = 'bottom-end',
		offset,
		onShow,
		contentType,
	} = props;
	const Wrapper = wrapper || React.Fragment;

	// TODO: Potentially refactor to use state from hook
	const { showContentTypeScreen } = useIsNthUserContentTypePageEnabled();

	const nudgeSpotlightRef = useRef<NudgeSpotlightRef>(null);
	const [isNudgeShowing, setIsNudgeShowing] = useState(false);

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		[
			NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_SELECTED_CONTENT_TYPE,
			NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_CONTENT_TYPE_SPOTLIGHT_SEEN,
			NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_CONTENT_TYPE_POPULAR_CONTENT_FOUND,
		],
		// skip condition to check showContentTypeScreen (serves as ff experiment check)
		!showContentTypeScreen,
	);

	const { nthUserContentTypeSpotlightSeen, nthUserContentTypePopularContentFound } =
		deserializeState(onboardingStateData);

	// Uses the onboarding state to determine if the nudge should be shown
	useEffect(() => {
		if (onboardingStateData && !onboardingStateLoading && !onboardingStateError) {
			if (!nthUserContentTypeSpotlightSeen) {
				setIsNudgeShowing(true);
			}
		}
	}, [
		onboardingStateData,
		onboardingStateLoading,
		onboardingStateError,
		nthUserContentTypeSpotlightSeen,
	]);

	useEffect(() => {
		// differs from tooltip because we need onboarding state to know when to show the original nudge
		experienceTracker.start({
			name: ONBOARDING_NUDGE_EXPERIENCE,
		});
	}, [isNudgeShowing, experienceTracker]);

	// Timer to account for lazily loaded components and to update tooltip position with a force rerender
	useEffect(() => {
		let timer: ReturnType<typeof setInterval>;
		if (!isNudgeShowing && nudgeSpotlightRef && nudgeSpotlightRef.current) {
			timer = autoAdjustPosition(nudgeSpotlightRef?.current?.forceUpdateCardPosition, {
				interval: 500,
				cycles: 10,
			});
		}

		return () => {
			clearInterval(timer);
		};
	}, [nudgeSpotlightRef, isNudgeShowing]);

	const handleOnDismiss = () => {
		// Can extend with onHide?.() or onClick?.(); from props
		fireEvent(createAnalyticsEvent, {
			type: 'sendTrackEvent',
			source,
			actionSubjectId,
			actionSubject: 'nudgeTooltip',
			action: 'dismissed',
			attributes: {
				contentType,
			},
		});

		setIsNudgeShowing(false);
	};

	const handleOnShow = () => {
		fireEvent(createAnalyticsEvent, {
			type: 'sendTrackEvent',
			source,
			actionSubjectId,
			actionSubject: 'nudgeTooltip',
			action: 'shown',
			attributes: {
				contentType,
			},
		});

		setOnboardingState({
			key: NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_CONTENT_TYPE_SPOTLIGHT_SEEN,
			value: 'true',
		});

		onShow?.();
	};

	// TODO: Add choreographer stop message if applicable (most likely not)
	const canChoreographRenderNudge = useChoreographedRender({
		messageId: 'basic-content-nudge-nth-user-onboarding',
	});

	if (onboardingStateError && !isErrorHandled(onboardingStateError)) {
		return (
			<>
				{children}
				<ErrorDisplay error={onboardingStateError} />
				<ExperienceFailure
					name={ONBOARDING_NUDGE_EXPERIENCE}
					error={onboardingStateError}
					attributes={{ source, actionSubjectId }}
				/>
			</>
		);
	}

	const headingMessage = nthUserContentTypePopularContentFound
		? i18n.popularHeading
		: i18n.nonPopularHeading;

	if (
		!isNudgeShowing ||
		onboardingStateLoading ||
		!canChoreographRenderNudge ||
		!showContentTypeScreen
	) {
		return <>{children}</>;
	}

	return (
		<Wrapper>
			<NudgeSpotlight
				{...props}
				actions={[
					{
						onClick: () => {
							handleOnDismiss();
						},
						text: 'Dismiss',
					},
				]}
				ref={nudgeSpotlightRef}
				heading={
					<FormattedMessage
						{...headingMessage}
						values={{
							contentType: intl.formatMessage(contentTypeMapping(contentType)),
						}}
					/>
				}
				content={
					<FormattedMessage
						{...i18n.contentDescription}
						values={{
							contentType: intl.formatMessage(contentTypeMapping(contentType)),
							pluralContentType: intl.formatMessage(contentTypeMapping(contentType, true)),
						}}
					/>
				}
				width={width}
				hidden={false}
				onHide={handleOnDismiss}
				onShow={handleOnShow}
				onClick={handleOnDismiss}
				zIndex={zIndex}
				position={position}
				offset={offset}
			>
				<div>{children}</div>
			</NudgeSpotlight>
			<ExperienceSuccess name={ONBOARDING_NUDGE_EXPERIENCE} />
		</Wrapper>
	);
};

/**
 * This nudge was created since it should not be lazy loaded when wrapping content that has "Paint" priority
 * It's a stripped down version specifically for Nth User Onboarding Content Types and not intended for other uses
 */
export const BasicContentNudge = (props: OnboardingNudgeProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const failedOnboardingNudge = useCallback(
		(error: Error) => {
			experienceTracker.fail({
				name: ONBOARDING_NUDGE_EXPERIENCE,
				error,
			});
		},
		[experienceTracker],
	);
	return (
		<GenericErrorBoundary
			attribution={Attribution.CC_ONBOARDING}
			onError={failedOnboardingNudge}
			renderOnError={() => {
				return <>{props.children}</>;
			}}
		>
			<OnboardingNthUserContentNudge {...props} />
		</GenericErrorBoundary>
	);
};
